import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, Button, Card, ActiveToggle, AvatarImage, ConfirmDialog, Field, InfoTip, PillButton, useBrand, brandDisplayName, apiErrorMessage } from '@aralere/shared-ui';
import { CreateLicenseExpenseDialog } from './CreateLicenseExpenseDialog';
import type {
  Service,
  Business,
  LicenseExpensePrompt,
  RecurringExpenseView,
  CreateServiceRequest,
  SsoType,
  ProvisioningModel,
  CostType,
  ServiceType,
  BillingTerm,
  RetentionModel,
  ManualAccessMethod,
  RetainerConfig,
  RetainerSla,
  ServiceActivePreflightResponse,
  ServiceGroupsResponse,
  CatalogTaxCategory,
} from '@aralere/shared-types';
import { useListControls } from './useListControls';
import { TAX_CATEGORY_OPTIONS, suggestTaxCategory, taxCategoryLabel } from './taxCategory';

// Title-Case labels for dropdowns. Stored values (left) are unchanged; only the label (right) is shown.
const SSO_OPTIONS = [
  ['entra-native', 'Entra Native'],
  ['saml', 'SAML'],
  ['oidc', 'OIDC'],
  ['admin-only', 'Admin Only'],
  ['none', 'None'],
] as const;
const PROV_OPTIONS = [
  ['tenancy-dependent', 'Tenancy Dependent'],
  ['aralere-tenant', 'Aralere Tenant'],
  ['manual', 'Manual'],
] as const;
const ACCESS_METHOD_OPTIONS = [
  ['', 'Not specified'],
  ['temp-password', 'Temporary password (one-time reveal)'],
  ['instructions', 'Sign-in instructions'],
] as const;
const COST_TYPE_OPTIONS = [
  ['fixed', 'Fixed'],
  ['metered', 'Metered'],
] as const;
const SERVICE_TYPE_OPTIONS = [
  ['regular', 'Regular'],
  ['retainer-use-it-or-lose-it', 'Basic Retainer'],
  ['retainer-liability', 'Retainer Liability'],
] as const;
// Lifecycle & billing term (s30 item 5 / 08 Q30) — descriptor only at Stage 2.
const TERM_OPTIONS = [
  ['monthly', 'Monthly'],
  ['annual', 'Annual'],
  ['flat', 'Flat (One Time)'],
] as const;
const RETENTION_OPTIONS = [
  ['', 'Unspecified'],
  ['deletion-required', 'Deletion Required'],
  ['data-retainable', 'Data Retainable'],
] as const;

const serviceTypeLabel = (t: string): string =>
  SERVICE_TYPE_OPTIONS.find(([v]) => v === t)?.[1] ?? t;
// Sales-tax category (04 #160, 02i T3.1): the select opens on a blank "Choose" row so a category is
// never applied silently; the name-based suggestion sits beside it as a one-click.
const TAX_CATEGORY_SELECT_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['', 'Choose what this service is for sales tax'],
  ...TAX_CATEGORY_OPTIONS,
];

type FormState = {
  id: string;
  name: string;
  shortName: string;
  clientFacingName: string;
  description: string;
  vendor: string;
  logoUrl: string;
  category: string;
  subCategory: string;
  // sales-tax category (04 #160) - '' = not chosen yet (a pre-build row reads as a professional service)
  taxCategory: '' | CatalogTaxCategory;
  ssoType: SsoType;
  launchUrl: string;
  provisioningModel: ProvisioningModel;
  licenseSku: string;
  // client access handoff (08 Q32, s39) — '' = not specified; meaningful only when ssoType 'none'
  manualAccessMethod: '' | ManualAccessMethod;
  accessInstructions: string;
  costType: CostType;
  defaultClientPrice: string;
  defaultCostToYou: string;
  currency: string;
  defaultBillingEntity: string;
  serviceType: ServiceType;
  sortOrder: string;
  complementaryServiceIds: string;
  // brand availability (s30 item 6) — availabilityAll true = all brands (stored null)
  availabilityAll: boolean;
  availableBrandIds: string[];
  // lifecycle & billing term (s30 item 5)
  billingTerm: BillingTerm;
  delicenseStopsBilling: boolean;
  neverProrate: boolean;
  retentionModel: '' | RetentionModel; // '' = unspecified (stored null)
  autoCancelDays: string; // '' = never
  warnLeadDays: string; // '' = inherit the brand default
  // retainer — use-it-or-lose-it
  includedHoursPerMonth: string;
  discountedOverageRate: string;
  // retainer — liability
  prepaidHours: string;
  autoRecurOnDepletion: boolean;
  expiryMonths: string;
  // shared retainer
  workTypeTags: string;
  // retainer SLA (optional; blank = none) — owner-introduced 2026-06-16
  slaResponseHours: string;
  slaResolutionDays: string;
};

function emptyForm(billingEntity: string): FormState {
  return {
    id: '',
    name: '',
    shortName: '',
    clientFacingName: '',
    description: '',
    vendor: '',
    logoUrl: '',
    category: '',
    subCategory: '',
    taxCategory: '',
    ssoType: 'none',
    launchUrl: '',
    provisioningModel: 'manual',
    licenseSku: '',
    manualAccessMethod: '',
    accessInstructions: '',
    costType: 'fixed',
    defaultClientPrice: '',
    defaultCostToYou: '0',
    currency: 'USD',
    defaultBillingEntity: billingEntity,
    serviceType: 'regular',
    sortOrder: '0',
    complementaryServiceIds: '',
    availabilityAll: true,
    availableBrandIds: [],
    billingTerm: 'monthly',
    delicenseStopsBilling: true,
    neverProrate: false,
    retentionModel: '',
    autoCancelDays: '',
    warnLeadDays: '',
    includedHoursPerMonth: '2',
    discountedOverageRate: '0',
    prepaidHours: '10',
    autoRecurOnDepletion: false,
    expiryMonths: '6',
    workTypeTags: '',
    slaResponseHours: '',
    slaResolutionDays: '',
  };
}

function formFromService(s: Service): FormState {
  const ru = s.retainerConfig as Record<string, unknown> | null;
  return {
    id: s.id,
    name: s.name,
    shortName: s.shortName,
    clientFacingName: s.clientFacingName ?? '',
    description: s.description,
    vendor: s.vendor,
    logoUrl: s.logoUrl,
    category: s.category,
    subCategory: s.subCategory,
    // A pre-build row has none stored; the select opens blank with the hint, never pre-filled.
    taxCategory: s.taxCategory ?? '',
    ssoType: s.ssoType,
    launchUrl: s.launchUrl ?? '',
    provisioningModel: s.provisioningModel,
    licenseSku: s.licenseSku ?? '',
    manualAccessMethod: s.manualAccessMethod ?? '',
    accessInstructions: s.accessInstructions ?? '',
    costType: s.costType,
    // Show the current price, but the admin can clear it to switch a service to margin pricing.
    defaultClientPrice: String(s.defaultClientPrice),
    defaultCostToYou: String(s.defaultCostToYou),
    currency: s.currency,
    defaultBillingEntity: s.defaultBillingEntity,
    serviceType: s.serviceType,
    sortOrder: String(s.sortOrder),
    complementaryServiceIds: (s.complementaryServiceIds ?? []).join(', '),
    availabilityAll: s.availableToBusinessIds == null,
    availableBrandIds: s.availableToBusinessIds ?? [],
    billingTerm: s.billingTerm ?? 'monthly',
    delicenseStopsBilling: s.delicenseStopsBilling ?? true,
    neverProrate: s.neverProrate ?? false,
    retentionModel: s.retentionModel ?? '',
    autoCancelDays: s.autoCancelAfterSuspendDays != null ? String(s.autoCancelAfterSuspendDays) : '',
    warnLeadDays: s.suspendWarningLeadDays != null ? String(s.suspendWarningLeadDays) : '',
    includedHoursPerMonth: ru && 'includedHoursPerMonth' in ru ? String(ru.includedHoursPerMonth) : '2',
    discountedOverageRate: ru && 'discountedOverageRate' in ru ? String(ru.discountedOverageRate) : '0',
    prepaidHours: ru && 'prepaidHours' in ru ? String(ru.prepaidHours) : '10',
    autoRecurOnDepletion: ru && 'autoRecurOnDepletion' in ru ? Boolean(ru.autoRecurOnDepletion) : false,
    expiryMonths: ru && 'expiryMonths' in ru && ru.expiryMonths != null ? String(ru.expiryMonths) : '6',
    workTypeTags: ru && Array.isArray(ru.workTypeTags) ? (ru.workTypeTags as string[]).join(', ') : '',
    slaResponseHours: slaField(ru, 'responseTimeHours'),
    slaResolutionDays: slaField(ru, 'resolutionTimeDays'),
  };
}

/** Read a nested retainerConfig.sla field back into a form string ('' = unset). */
function slaField(ru: Record<string, unknown> | null, key: 'responseTimeHours' | 'resolutionTimeDays'): string {
  const sla = ru && typeof ru.sla === 'object' && ru.sla !== null ? (ru.sla as Record<string, unknown>) : null;
  const v = sla ? sla[key] : null;
  return v == null ? '' : String(v);
}

const num = (s: string): number => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
const intOrNull = (s: string): number | null => {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
};
const csv = (s: string): string[] =>
  s.split(',').map((x) => x.trim()).filter(Boolean);

/** Build the optional SLA object from the form (null when both fields are blank — "no SLA"). */
function buildSla(f: FormState): RetainerSla | null {
  const resp = f.slaResponseHours.trim() === '' ? null : num(f.slaResponseHours);
  const reso = f.slaResolutionDays.trim() === '' ? null : num(f.slaResolutionDays);
  if (resp == null && reso == null) return null;
  return { responseTimeHours: resp, resolutionTimeDays: reso };
}

function buildRetainerConfig(f: FormState): RetainerConfig {
  if (f.serviceType === 'retainer-use-it-or-lose-it') {
    return {
      includedHoursPerMonth: num(f.includedHoursPerMonth),
      resetMonthly: true,
      discountedOverageRate: num(f.discountedOverageRate),
      workTypeTags: csv(f.workTypeTags),
      sla: buildSla(f),
    };
  }
  if (f.serviceType === 'retainer-liability') {
    return {
      prepaidHours: num(f.prepaidHours),
      autoRecurOnDepletion: f.autoRecurOnDepletion,
      expiryMonths: intOrNull(f.expiryMonths),
      workTypeTags: csv(f.workTypeTags),
      deferredRevenue: true,
      sla: buildSla(f),
    };
  }
  return null;
}

function buildPayload(f: FormState): CreateServiceRequest {
  const priceStr = f.defaultClientPrice.trim();
  return {
    id: f.id.trim() || undefined,
    name: f.name.trim(),
    shortName: f.shortName.trim(),
    // Blank → null; clients then see the full `name`.
    clientFacingName: f.clientFacingName.trim() || null,
    description: f.description.trim(),
    vendor: f.vendor.trim(),
    logoUrl: f.logoUrl.trim(),
    category: f.category.trim(),
    subCategory: f.subCategory.trim(),
    // Sales-tax category (04 #160): submit() refuses a blank before this runs, so the payload always
    // carries one; the undefined branch only keeps the type honest.
    taxCategory: f.taxCategory === '' ? undefined : f.taxCategory,
    ssoType: f.ssoType,
    launchUrl: f.launchUrl.trim() || null,
    provisioningModel: f.provisioningModel,
    licenseSku: f.licenseSku.trim() || null,
    // Client access handoff (08 Q32) — meaningful only for ssoType 'none'; an SSO type nulls it.
    manualAccessMethod: f.ssoType === 'none' && f.manualAccessMethod !== '' ? f.manualAccessMethod : null,
    accessInstructions: f.accessInstructions.trim() || null,
    costType: f.costType,
    // Blank → null → server auto-prices from the billing entity's margin (spec §5.8).
    defaultClientPrice: priceStr === '' ? null : num(priceStr),
    defaultCostToYou: num(f.defaultCostToYou),
    currency: f.currency.trim() || 'USD',
    defaultBillingEntity: f.defaultBillingEntity.trim(),
    serviceType: f.serviceType,
    retainerConfig: buildRetainerConfig(f),
    complementaryServiceIds: csv(f.complementaryServiceIds),
    sortOrder: num(f.sortOrder),
    // Brand availability (s30 item 6) — all brands = null; a restriction = the checked subset.
    availableToBusinessIds: f.availabilityAll ? null : f.availableBrandIds,
    // Lifecycle & billing term (s30 item 5) — blanks mean never / inherit (stored null).
    billingTerm: f.billingTerm,
    delicenseStopsBilling: f.delicenseStopsBilling,
    neverProrate: f.neverProrate,
    retentionModel: f.retentionModel === '' ? null : f.retentionModel,
    autoCancelAfterSuspendDays: f.autoCancelDays.trim() === '' ? null : intOrNull(f.autoCancelDays),
    suspendWarningLeadDays: f.warnLeadDays.trim() === '' ? null : intOrNull(f.warnLeadDays),
  };
}

export function AdminServices(): JSX.Element {
  const { brand } = useBrand();
  const defaultBillingEntity = brand?.id ?? 'biz-aralere';

  const [services, setServices] = useState<Service[] | null>(null);
  const [assignedCounts, setAssignedCounts] = useState<Record<string, number>>({});
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm(defaultBillingEntity));
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  // 04 #10: the edited service's UPLOADED logo (live server state, not a staged form value —
  // upload/remove are IMMEDIATE actions, the Finding-G Option-A pattern). logoVersion cache-busts
  // the preview after a replace (the serve route is short-cached).
  const [editLogoRef, setEditLogoRef] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  // s41: upload/remove errors render INSIDE the logo block — the bottom form error sat out of
  // view below the fold, so the first prod failure looked like a silent no-op.
  const [logoError, setLogoError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [svc, biz] = await Promise.all([
        api.admin.listServices(),
        api.admin.listBusinesses(),
      ]);
      setServices(svc.services);
      setAssignedCounts(svc.assignedClientCounts);
      setBusinesses(biz.businesses);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (formOpen) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [formOpen]);

  const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  // 04 #160: the name-based tax-category suggestion (the server's rule, mirrored) - shown, never applied.
  const taxSuggestion = suggestTaxCategory(form.name);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(defaultBillingEntity));
    setEditLogoRef(null);
    setLogoError(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (s: Service) => {
    setEditingId(s.id);
    setForm(formFromService(s));
    setEditLogoRef(s.logoBlobRef ?? null);
    setLogoError(null);
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!marginOnForEntity && form.defaultClientPrice.trim() === '') {
      setFormError(
        `Enter a client price${selectedBiz ? ` for ${brandDisplayName(selectedBiz.name)}` : ''}. Its margin calculator is off, so the price can't be auto-filled. Enter 0 for a free service, or turn margin on in Settings.`,
      );
      return;
    }
    if (!form.availabilityAll && form.availableBrandIds.length === 0) {
      setFormError('Brand availability: select at least one brand, or switch back to all brands.');
      return;
    }
    // 04 #160 (02i T3.1): a new or edited service cannot save without a tax category. The suggestion
    // beside the select is a click away, never applied for the admin.
    if (form.taxCategory === '') {
      setFormError('Tax category: choose what this service is for sales-tax purposes (the suggestion under the field is one click).');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload(form);
      if (!payload.name || !payload.shortName) {
        throw new Error('Name and short name are required.');
      }
      // s30 item 7: the pre-save catalog model, from the loaded list (the form value is the edit).
      const prevModel = editingId ? services?.find((sv) => sv.id === editingId)?.provisioningModel : undefined;
      if (editingId) {
        // PATCH uses the path id; the server ignores body.id, so sending the full payload is fine.
        await api.admin.updateService(editingId, payload);
      } else {
        await api.admin.createService(payload);
      }
      // Promotion prompt (s30 item 7): the save flipped manual -> provisioned, so offer the one-time
      // holder-provisioning run. Best-effort; the panel's button covers a skipped or failed fetch.
      if (editingId && prevModel === 'manual' && payload.provisioningModel !== 'manual') {
        try {
          const gi = await api.admin.getServiceGroups(editingId);
          if (gi.activeRowCount > 0) {
            setPromote({ serviceId: gi.serviceId, serviceName: payload.name, info: gi });
          }
        } catch {
          /* the panel button covers it */
        }
      }
      await load();
      closeForm();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── 04 #10: service-logo image upload — IMMEDIATE actions on the edited service (not staged
  //    behind Save, since the upload needs the service id and mirrors the client-logo lane).
  //    The uploaded image takes precedence over the URL; the URL field is never touched.
  const onServiceLogoFile = async (file: File | null): Promise<void> => {
    if (!file || !editingId) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const res = await api.admin.uploadServiceLogo(editingId, file);
      setEditLogoRef(res.logoBlobRef);
      setLogoVersion((v) => v + 1);
      await load();
    } catch (err) {
      setLogoError(apiErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  const removeUploadedLogo = async (): Promise<void> => {
    if (!editingId) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      await api.admin.removeServiceLogo(editingId);
      setEditLogoRef(null);
      setLogoVersion((v) => v + 1);
      await load();
    } catch (err) {
      setLogoError(apiErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  // ── s30 item 4 (08 Q29): the Active toggle is a BULK CASCADE — preflight first, then an in-app
  //    confirm (counts + the package-member warning-with-options on disable; the restore-vs-clean-
  //    slate prompt on re-enable when suspended holders are pending). Replaces window.confirm.
  const [info, setInfo] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    kind: 'disable' | 'enable';
    ids: string[];
    pre: ServiceActivePreflightResponse[];
    fromBulk: boolean;
  } | null>(null);
  const [cleanSlate, setCleanSlate] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // ── s30 item 7 (08 Q31): the manual->provisioned PROMOTION prompt + the Entra-groups panel's
  //    reload token. The prompt fires once after a save that flips the model off 'manual'; skipping
  //    it is recoverable from the panel's "Provision current holders" button.
  const [promote, setPromote] = useState<{ serviceId: string; serviceName: string; info: ServiceGroupsResponse } | null>(null);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [groupsReload, setGroupsReload] = useState(0);

  const runPromote = async (): Promise<void> => {
    if (!promote) return;
    setPromoteBusy(true);
    setError(null);
    try {
      const res = await api.admin.provisionServiceHolders(promote.serviceId);
      const failNote =
        res.failedCount > 0
          ? ` ${res.failedCount} failed and will be retried automatically (you were emailed).`
          : '';
      setInfo(
        `Provisioned ${res.provisionedCount} of ${res.activeRowCount} holder assignment${res.activeRowCount === 1 ? '' : 's'} across ${res.affectedClientCount} client${res.affectedClientCount === 1 ? '' : 's'}.${failNote}`,
      );
      setPromote(null);
      setGroupsReload((n) => n + 1);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setPromoteBusy(false);
    }
  };

  const toggleActive = async (s: Service) => {
    setBusyId(s.id);
    setError(null);
    setInfo(null);
    try {
      const pre = await api.admin.getServiceActivePreflight(s.id);
      if (s.active) {
        setCleanSlate(false);
        setConfirmState({ kind: 'disable', ids: [s.id], pre: [pre], fromBulk: false });
      } else if (pre.pendingBatch) {
        setCleanSlate(false);
        setConfirmState({ kind: 'enable', ids: [s.id], pre: [pre], fromBulk: false });
      } else {
        // Plain re-enable: nothing was suspended by this disable, no prompt needed.
        await api.admin.setServiceActive(s.id, true);
        await load();
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const runConfirmed = async (): Promise<void> => {
    if (!confirmState) return;
    setConfirmBusy(true);
    setError(null);
    try {
      let clients = 0;
      let failures = 0;
      // 04 #116 / #117: the batch size is NOT the restored count and NOT the suspended-and-notified
      // count. Carry the server's own split figures rather than re-deriving one number for all three.
      let alreadySuspended = 0;
      let restored = 0;
      let heldByDebt = 0;
      for (const id of confirmState.ids) {
        const res =
          confirmState.kind === 'disable'
            ? await api.admin.setServiceActive(id, false)
            : await api.admin.setServiceActive(id, true, cleanSlate ? 'clean-slate' : 'restore-all');
        clients += res.bulk?.affectedClientCount ?? 0;
        failures += res.bulk?.failedRowCount ?? 0;
        alreadySuspended += res.bulk?.alreadySuspendedRowCount ?? 0;
        restored += res.bulk?.restoredClientCount ?? res.bulk?.affectedClientCount ?? 0;
        heldByDebt += res.bulk?.heldByDebtClientCount ?? 0;
      }
      const n = confirmState.ids.length;
      const label = n === 1 ? 'service' : `${n} services`;
      const failNote =
        failures > 0
          ? ` ${failures} provisioning operation${failures === 1 ? '' : 's'} failed and will be retried automatically (you were emailed).`
          : '';
      // 04 #117: the operator summary email has always carried this line; the banner never did, so
      // the stamped rows were invisible on screen (04 #112 rule 5's operator-visibility half).
      const stampedNote =
        alreadySuspended > 0
          ? ` ${alreadySuspended} assignment${alreadySuspended === 1 ? ' was' : 's were'} already suspended for non-payment and stayed that way; ${alreadySuspended === 1 ? 'it is' : 'they are'} recorded in this batch and re-enabling will leave ${alreadySuspended === 1 ? 'it' : 'them'} suspended until the balance is paid.`
          : '';
      // 04 #116: name the two populations separately. Restoring a service does not settle a debt.
      const heldNote =
        heldByDebt > 0
          ? ` ${heldByDebt} client${heldByDebt === 1 ? ' stays' : 's stay'} suspended until their past due balance is paid.`
          : '';
      setInfo(
        confirmState.kind === 'disable'
          ? `Disabled the ${label}. ${clients} client${clients === 1 ? ' was' : 's were'} suspended and notified.${stampedNote}${failNote}`
          : cleanSlate
            ? `Re-enabled the ${label} with a clean slate. ${clients} client${clients === 1 ? ' stays' : 's stay'} suspended.`
            : `Re-enabled the ${label}. ${restored} client${restored === 1 ? ' was' : 's were'} restored and notified.${heldNote}${failNote}`,
      );
      if (confirmState.fromBulk) clear();
      setConfirmState(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  const isRetainer = form.serviceType !== 'regular';

  const billingOptions: ReadonlyArray<readonly [string, string]> = businesses.length
    ? businesses.map((b) => [b.id, brandDisplayName(b.name)] as const)
    : [[form.defaultBillingEntity, form.defaultBillingEntity] as const];

  // Managed dropdowns (08 Q8/Q9): the catalog has no `categories` container, so the picker is the
  // set of distinct category / sub-category values already in use — pick an existing one or type a
  // new one (datalist combobox). No schema change.
  const categoryOptions = Array.from(new Set((services ?? []).map((s) => s.category).filter(Boolean))).sort();
  const subCategoryOptions = Array.from(new Set((services ?? []).map((s) => s.subCategory).filter(Boolean))).sort();

  // When the selected billing entity's margin is OFF, a blank price can't be auto-resolved (spec
  // §5.8) — so require it client-side with a clear prompt instead of bouncing off a server 400.
  const selectedBiz = businesses.find((b) => b.id === form.defaultBillingEntity);
  const marginOnForEntity = !!selectedBiz?.standardMargin.enabled;

  // ── list filters + pagination + multi-select (mirrors the Packages list controls; reuses ./useListControls) ──
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const q = query.trim().toLowerCase();
  const filtered = (services ?? []).filter((s) => {
    if (q && !`${s.name} ${s.shortName} ${s.clientFacingName ?? ''} ${s.id}`.toLowerCase().includes(q)) return false;
    if (catFilter && s.category !== catFilter) return false;
    if (subFilter && s.subCategory !== subFilter) return false;
    if (statusFilter === 'active' && !s.active) return false;
    if (statusFilter === 'inactive' && s.active) return false;
    return true;
  });
  const { page, setPage, totalPages, pageItems, selected, isSelected, toggle, setMany, clear } = useListControls(filtered, 25);
  const pageIds = pageItems.map((s) => s.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => isSelected(id));

  // Cascading filter options: each dropdown lists only values still reachable given the OTHER
  // dropdown's selection, so picking a category narrows the sub-category list and vice-versa.
  const filterCatOptions = Array.from(
    new Set((services ?? []).filter((s) => !subFilter || s.subCategory === subFilter).map((s) => s.category).filter(Boolean)),
  ).sort();
  const filterSubOptions = Array.from(
    new Set((services ?? []).filter((s) => !catFilter || s.category === catFilter).map((s) => s.subCategory).filter(Boolean)),
  ).sort();

  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkSetActive = async (next: boolean): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setError(null);
    setInfo(null);
    try {
      const pres = await Promise.all(ids.map((id) => api.admin.getServiceActivePreflight(id)));
      if (!next) {
        setCleanSlate(false);
        setConfirmState({ kind: 'disable', ids, pre: pres, fromBulk: true });
      } else if (pres.some((p) => p.pendingBatch)) {
        setCleanSlate(false);
        setConfirmState({ kind: 'enable', ids, pre: pres, fromBulk: true });
      } else {
        for (const id of ids) await api.admin.setServiceActive(id, true);
        clear();
        await load();
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBulkBusy(false);
    }
  };

  // Totals for the confirm dialogs (summed across the selected services).
  const confirmClientTotal =
    confirmState?.pre.reduce(
      (acc, p) =>
        acc + (confirmState.kind === 'disable' ? p.affectedClientCount : (p.pendingBatch?.clientCount ?? 0)),
      0,
    ) ?? 0;
  const confirmAffectedPackages = confirmState
    ? confirmState.pre.flatMap((p) => p.affectedPackages)
    : [];
  // 04 #116: what the restore will ACTUALLY do, from the server's own predicate. The older
  // `clientCount` is the batch size, which counts the units stamped in tag-only while
  // non-payment-suspended; promising to restore and notify those was the defect.
  const confirmRestorableTotal =
    confirmState?.pre.reduce(
      (acc, p) => acc + (p.pendingBatch?.restorableClientCount ?? p.pendingBatch?.clientCount ?? 0),
      0,
    ) ?? 0;
  const confirmHeldTotal =
    confirmState?.pre.reduce((acc, p) => acc + (p.pendingBatch?.heldByDebtClientCount ?? 0), 0) ?? 0;
  const confirmDisabledDate = confirmState?.pre.find((p) => p.pendingBatch)?.pendingBatch?.at ?? null;
  const confirmLabelText = confirmState
    ? confirmState.ids.length === 1
      ? `“${confirmState.pre[0]?.service.name ?? confirmState.ids[0]}”`
      : `${confirmState.ids.length} services`
    : '';

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Services Catalog</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {services?.length ?? 0} service{(services?.length ?? 0) === 1 ? '' : 's'} · includes inactive
          </p>
        </div>
        <Button onClick={openCreate}>New service</Button>
      </header>

      {formOpen ? (
        <div ref={formRef}>
        <Card>
          <form onSubmit={submit} className="space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl">
                {editingId ? 'Edit service' : 'New service'}
              </h2>
              {editingId ? (
                <span className="text-xs text-[var(--color-muted)] font-mono">{editingId}</span>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Name (internal)" required>
                <Input value={form.name} onChange={(v) => update({ name: v })} placeholder="Microsoft 365 Business Standard" required />
              </Field>
              <Field label="Short name" required>
                <Input value={form.shortName} onChange={(v) => update({ shortName: v })} placeholder="M365 BS" required />
              </Field>
              <Field label="Client-facing name (Blank = Uses Name)">
                <Input
                  value={form.clientFacingName}
                  onChange={(v) => update({ clientFacingName: v })}
                  placeholder={form.name || 'Shown to clients'}
                />
              </Field>
            </div>

            <Field label="Description" required>
              <textarea
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={2}
                required
                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
              />
            </Field>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Vendor" required>
                <Input value={form.vendor} onChange={(v) => update({ vendor: v })} placeholder="Microsoft" required />
              </Field>
              <Field
                label="Logo URL (optional)"
                tip="Paste a hosted image URL, upload an image below, or both. An uploaded image takes precedence; the URL is kept as the fallback, so removing the upload brings it back. With neither, tiles show the service's initial letter."
              >
                <Input value={form.logoUrl} onChange={(v) => update({ logoUrl: v })} placeholder="https://…/logo.svg" />
              </Field>
              <Field label="Category" required>
                <Input value={form.category} onChange={(v) => update({ category: v })} placeholder="productivity" required list="svc-categories" />
              </Field>
              <Field label="Sub-category" required>
                <Input value={form.subCategory} onChange={(v) => update({ subCategory: v })} placeholder="office-suite" required list="svc-subcategories" />
              </Field>
              <Field
                label="Tax category"
                required
                tip="What the service IS for sales-tax purposes, not who provides it: resold Microsoft 365 seats are prewritten software whichever brand sells them. From January 2027 California taxes prewritten software / SaaS and digital products; professional services and custom software stay untaxed. Taxability is decided per client and per invoice date from this category and the client's state, so the same service can be taxed for one client and not another. A service without a category counts as a professional service (untaxed) until you set one; the December tax review lists every service by category."
              >
                <Select
                  value={form.taxCategory}
                  onChange={(v) => update({ taxCategory: v as '' | CatalogTaxCategory })}
                  options={TAX_CATEGORY_SELECT_OPTIONS}
                />
                {taxSuggestion && taxSuggestion.category !== form.taxCategory ? (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                    <span>
                      Suggested from the name: <strong>{taxCategoryLabel(taxSuggestion.category)}</strong> (matched {taxSuggestion.why}).
                    </span>
                    <PillButton size="xs" onClick={() => update({ taxCategory: taxSuggestion.category })}>
                      Use suggestion
                    </PillButton>
                  </p>
                ) : null}
                {form.taxCategory === '' && !taxSuggestion ? (
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    {editingId
                      ? 'Not set yet. Until you choose one this service counts as a professional service (untaxed everywhere).'
                      : 'Required. Nothing in the name suggests a category; choose one.'}
                  </p>
                ) : form.taxCategory === '' && editingId ? (
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Not set yet. Until you choose one this service counts as a professional service (untaxed everywhere).
                  </p>
                ) : null}
              </Field>
              <datalist id="svc-categories">
                {categoryOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <datalist id="svc-subcategories">
                {subCategoryOptions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            {editingId ? (
              <div className="flex items-center gap-4 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4">
                <AvatarImage
                  src={
                    editLogoRef
                      ? `/api/service-logo?serviceId=${encodeURIComponent(editingId)}&v=${logoVersion}`
                      : form.logoUrl.trim() || null
                  }
                  name={form.name || editingId}
                  size={44}
                  shape="square"
                  alt="Service logo preview"
                />
                <div className="flex-1 text-sm">
                  <p className="font-medium">
                    Logo image
                    <InfoTip text="Upload an image file for this service's logo. It is stored by the platform and shown everywhere in place of the Logo URL. The URL stays saved, so removing the uploaded image falls back to it." />
                  </p>
                  <p className="text-xs text-[var(--color-muted)]">
                    {editLogoRef
                      ? 'Using the uploaded image. The Logo URL is kept as the fallback.'
                      : form.logoUrl.trim()
                        ? 'Using the Logo URL. Upload an image to take precedence over it.'
                        : 'No logo yet. Tiles show the initial letter until a URL or image is set.'}
                  </p>
                  {logoError ? <p className="mt-1 text-xs text-[var(--tint-red-text)]">{logoError}</p> : null}
                </div>
                <label className="cursor-pointer rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-1.5 text-sm hover:bg-[rgb(var(--edge-rgb)/0.03)]">
                  {logoBusy ? 'Working…' : editLogoRef ? 'Replace image' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={logoBusy}
                    onChange={(e) => {
                      void onServiceLogoFile(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                  />
                </label>
                {editLogoRef ? (
                  <Button type="button" onClick={() => void removeUploadedLogo()} disabled={logoBusy}>
                    Remove image
                  </Button>
                ) : null}
              </div>
            ) : null}

            {!editingId ? (
              <Field label="Service ID (optional, derived from name if blank)">
                <Input value={form.id} onChange={(v) => update({ id: v })} placeholder="svc-m365-business-standard" mono />
              </Field>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="SSO type" required>
                <Select
                  value={form.ssoType}
                  onChange={(v) => update({ ssoType: v as SsoType })}
                  options={SSO_OPTIONS}
                />
              </Field>
              <Field label="Launch URL (blank = status-only)">
                <Input value={form.launchUrl} onChange={(v) => update({ launchUrl: v })} placeholder="https://www.office.com" />
              </Field>
              <Field
                label="Provisioning model"
                required
                tip="Manual services are set up by hand and have no Entra group. Changing manual to a provisioned model is a catalog edit only; after saving you are offered a one-time run that provisions the current holders. Changing a provisioned model to manual grandfathers existing holders: they keep their access and group memberships, and suspending one still revokes it. Nothing is deprovisioned by the change itself."
              >
                <Select
                  value={form.provisioningModel}
                  onChange={(v) => update({ provisioningModel: v as ProvisioningModel })}
                  options={PROV_OPTIONS}
                />
              </Field>
              <Field label="License SKU (optional)">
                <Input value={form.licenseSku} onChange={(v) => update({ licenseSku: v })} mono />
              </Field>
              <Field
                label="Billing entity"
                required
                tip="Which business's books this service bills to by default. Revenue and cost are attributed to this entity, and its margin calculator prices the service when the client price is blank. It does not control who can take the service; that is Brand availability below. It can be overridden per client assignment."
              >
                <Select
                  value={form.defaultBillingEntity}
                  onChange={(v) => update({ defaultBillingEntity: v })}
                  options={billingOptions}
                />
              </Field>
            </div>

            {businesses.length > 1 ? (
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-3">
                <p className="text-sm font-medium">
                  Brand availability
                  <InfoTip text="Which brands can take this service up. Available to all brands is the default and automatically covers any brand added later. Restricting it hides the service from other brands' client catalogs and blocks assigning it to their clients. Clients already holding it are not affected." />
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.availabilityAll}
                    onChange={(e) => update({ availabilityAll: e.target.checked })}
                  />
                  Available to all brands
                </label>
                {!form.availabilityAll ? (
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {businesses.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.availableBrandIds.includes(b.id)}
                          onChange={(e) =>
                            update({
                              availableBrandIds: e.target.checked
                                ? [...form.availableBrandIds, b.id]
                                : form.availableBrandIds.filter((id) => id !== b.id),
                            })
                          }
                        />
                        {brandDisplayName(b.name)}
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <Field
                label="Client access method"
                tip="How a client gets into a service that is not SSO. Temporary password: you issue one-time sign-in details from the client's services panel; they are stored in a secure vault, shown to the client exactly once, and can never be viewed again, including by you. Sign-in instructions: the client is shown the instructions text. SSO services hand off sign-in automatically, so this only applies when SSO type is None."
              >
                <Select
                  value={form.ssoType === 'none' ? form.manualAccessMethod : ''}
                  onChange={(v) => update({ manualAccessMethod: v as '' | ManualAccessMethod })}
                  options={ACCESS_METHOD_OPTIONS}
                  disabled={form.ssoType !== 'none'}
                />
              </Field>
              {form.ssoType === 'none' && form.manualAccessMethod !== '' ? (
                <Field label="Sign-in instructions (shown to the client)">
                  <textarea
                    value={form.accessInstructions}
                    onChange={(e) => update({ accessInstructions: e.target.value })}
                    rows={2}
                    maxLength={2000}
                    placeholder="e.g. Sign in with the email we set up for you. Use Forgot password on your first visit."
                    className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
                  />
                </Field>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Cost type" required>
                <Select value={form.costType} onChange={(v) => update({ costType: v as CostType })} options={COST_TYPE_OPTIONS} />
              </Field>
              <Field label="Cost ($)" required>
                <Input value={form.defaultCostToYou} onChange={(v) => update({ defaultCostToYou: v })} type="number" />
              </Field>
              <Field label="Client price ($)" required={!marginOnForEntity}>
                <Input
                  value={form.defaultClientPrice}
                  onChange={(v) => update({ defaultClientPrice: v })}
                  type="number"
                  placeholder={marginOnForEntity ? 'blank = auto from margin' : 'required (margin off)'}
                />
              </Field>
            </div>
            <p className="-mt-2 text-xs text-[var(--color-muted)]">
              {marginOnForEntity
                ? "Leave blank to auto-price from this billing entity's margin (managed in Settings), or enter a price to set it manually. Enter 0 for a free service."
                : `Margin is off for ${selectedBiz ? brandDisplayName(selectedBiz.name) : 'this billing entity'}: enter a price (0 for a free service). Turn margin on in Settings to auto-price.`}
            </p>

            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Currency">
                <Input value={form.currency} onChange={(v) => update({ currency: v })} />
              </Field>
              <Field label="Sort order">
                <Input value={form.sortOrder} onChange={(v) => update({ sortOrder: v })} type="number" />
              </Field>
              <Field label="Service type" required>
                <Select
                  value={form.serviceType}
                  onChange={(v) => update({ serviceType: v as ServiceType })}
                  options={SERVICE_TYPE_OPTIONS}
                />
              </Field>
            </div>

            <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
              <p className="text-sm font-medium">Lifecycle and billing term</p>
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Billing term" tip="How this service bills. Monthly renews each month. Annual runs to a per-client renewal date, set on each client's assignment. Flat is a one-time charge.">
                  <Select value={form.billingTerm} onChange={(v) => update({ billingTerm: v as BillingTerm })} options={TERM_OPTIONS} />
                </Field>
                <Field label="Retention model" tip="What happens to stored data while the service is off. Deletion Required means the provider may permanently delete data, so reactivation warnings are strong. Data Retainable means data is expected to survive. Unspecified keeps the generic warning.">
                  <Select value={form.retentionModel} onChange={(v) => update({ retentionModel: v as '' | RetentionModel })} options={RETENTION_OPTIONS} />
                </Field>
                <label className="flex items-center gap-2 text-sm md:mt-7">
                  <input
                    type="checkbox"
                    checked={form.delicenseStopsBilling}
                    onChange={(e) => update({ delicenseStopsBilling: e.target.checked })}
                  />
                  Delicensing stops billing
                  <InfoTip text="When checked, suspending or delicensing a holder stops their billing for this service. Uncheck for services that must be paid through the end of their term even while suspended, which is typical for annual licenses." />
                </label>
                <label className="flex items-center gap-2 text-sm md:mt-7">
                  <input
                    type="checkbox"
                    checked={form.neverProrate}
                    onChange={(e) => update({ neverProrate: e.target.checked })}
                  />
                  Never prorate
                  <InfoTip text="When checked, this service always bills its full monthly price for any month it is active for any part of, with no first-month proration and no credit for disabled days. It wins over the brand's proration settings. Ideal for flat monthly retainers." />
                </label>
                <Field label="Auto-cancel after (days, blank = never)" tip="If a client's assignment stays suspended this many days, it is auto-cancelled by the billing sweep. The value is stored now; enforcement runs in a later phase. Blank means never auto-cancel.">
                  <Input value={form.autoCancelDays} onChange={(v) => update({ autoCancelDays: v })} type="number" placeholder="e.g. 60" />
                </Field>
                <Field label="Warning lead (days, blank = brand default)" tip="How many days before the auto-cancel date the client is warned about potential data loss. Blank inherits the brand default from Settings.">
                  <Input value={form.warnLeadDays} onChange={(v) => update({ warnLeadDays: v })} type="number" placeholder="brand default" />
                </Field>
              </div>
              <p className="text-xs text-[var(--color-muted)]">
                These describe lifecycle policy only. Enforcement (auto-cancel, warning emails, annual billed-through)
                runs in the billing phase; nothing is scheduled or charged when you save. This section is the{' '}
                <strong>client side</strong> of the ledger: how you bill for the service and what happens to a
                client's access. What the service costs <em>you</em> (seats, price per seat) is the other side; it
                lives on the linked license expense, in the License and seats panel below and on the Expenses page.
              </p>
            </div>

            {editingId ? (
              <LicenseSeatsPanel
                serviceId={editingId}
                serviceName={form.name}
                catalogCost={form.defaultCostToYou}
                businessId={form.defaultBillingEntity}
                vendorName={form.vendor}
                businesses={businesses}
                onUseLicensePrice={(p) => update({ defaultCostToYou: p })}
              />
            ) : null}

            {editingId ? (
              <EntraGroupsPanel
                serviceId={editingId}
                formModel={form.provisioningModel}
                reloadToken={groupsReload}
                onRequestProvision={(gi) =>
                  setPromote({ serviceId: gi.serviceId, serviceName: form.name || gi.serviceId, info: gi })
                }
              />
            ) : null}

            <Field label="Complementary service IDs (comma-separated, optional)">
              <Input
                value={form.complementaryServiceIds}
                onChange={(v) => update({ complementaryServiceIds: v })}
                placeholder="svc-exchange-online, svc-1password-business"
                mono
              />
            </Field>

            {isRetainer ? (
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
                <p className="text-sm font-medium">Retainer configuration</p>
                {form.serviceType === 'retainer-use-it-or-lose-it' ? (
                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Included hours / month">
                      <Input value={form.includedHoursPerMonth} onChange={(v) => update({ includedHoursPerMonth: v })} type="number" />
                    </Field>
                    <Field label="Discounted overage rate ($/hr)">
                      <Input value={form.discountedOverageRate} onChange={(v) => update({ discountedOverageRate: v })} type="number" />
                    </Field>
                    <Field label="Work-type tags (comma-separated)">
                      <Input value={form.workTypeTags} onChange={(v) => update({ workTypeTags: v })} placeholder="it-support" />
                    </Field>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-3">
                    <Field label="Prepaid hours">
                      <Input value={form.prepaidHours} onChange={(v) => update({ prepaidHours: v })} type="number" />
                    </Field>
                    <Field label="Expiry (months)">
                      <Input value={form.expiryMonths} onChange={(v) => update({ expiryMonths: v })} type="number" />
                    </Field>
                    <Field label="Work-type tags (comma-separated)">
                      <Input value={form.workTypeTags} onChange={(v) => update({ workTypeTags: v })} placeholder="networking" />
                    </Field>
                    <label className="flex items-center gap-2 text-sm md:col-span-3">
                      <input
                        type="checkbox"
                        checked={form.autoRecurOnDepletion}
                        onChange={(e) => update({ autoRecurOnDepletion: e.target.checked })}
                      />
                      Auto-recur on depletion
                    </label>
                  </div>
                )}
                <div className="space-y-3 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-4">
                  <p className="text-sm font-medium">SLA targets (optional)</p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Response time (hours)">
                      <Input value={form.slaResponseHours} onChange={(v) => update({ slaResponseHours: v })} type="number" placeholder="e.g. 4" />
                    </Field>
                    <Field label="Resolution time (days)">
                      <Input value={form.slaResolutionDays} onChange={(v) => update({ slaResolutionDays: v })} type="number" placeholder="e.g. 3" />
                    </Field>
                  </div>
                  <p className="text-xs text-[var(--color-muted)]">
                    Leave both blank for no SLA. Included in your signup notification; clients see only hours, overage, and work type.
                  </p>
                </div>
                <p className="text-xs text-[var(--color-muted)]">
                  Liability/prepaid retainers are built but off by default (spec §8.10); only offer them deliberately.
                </p>
              </div>
            ) : null}

            {formError ? (
              <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-4 py-3 text-sm text-[var(--tint-red-text)]">
                {formError}
              </div>
            ) : null}

            <div className="flex gap-3 pt-1">
              <Button type="submit" loading={submitting} disabled={submitting}>
                {editingId ? 'Save changes' : 'Create service'}
              </Button>
              <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
        </div>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">Error: {error}</p> : null}
      {info ? (
        <p className="rounded-md border border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)] px-3 py-2 text-sm text-[var(--tint-green-text)]">{info}</p>
      ) : null}
      {loading ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {services && services.length === 0 && !loading ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            No services yet. Click <span className="font-medium">New service</span> to create the first one.
          </p>
        </Card>
      ) : null}

      {services && services.length > 0 ? (
        <div className="space-y-3">
          {/* filter toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search name or ID…"
              className="min-w-[12rem] rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            />
            <select
              value={catFilter}
              onChange={(e) => {
                const nc = e.target.value;
                setCatFilter(nc);
                setPage(0);
                if (subFilter && nc && !(services ?? []).some((s) => s.category === nc && s.subCategory === subFilter)) {
                  setSubFilter('');
                }
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">All categories</option>
              {filterCatOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={subFilter}
              onChange={(e) => {
                const ns = e.target.value;
                setSubFilter(ns);
                setPage(0);
                if (catFilter && ns && !(services ?? []).some((s) => s.subCategory === ns && s.category === catFilter)) {
                  setCatFilter('');
                }
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="">All sub-categories</option>
              {filterSubOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                setPage(0);
              }}
              className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <span className="text-xs text-[var(--color-muted)]">
              {filtered.length} of {services.length}
            </span>
          </div>

          {/* bulk-action bar (selection persists across pages + filters) */}
          {selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/5 px-3 py-2 text-sm">
              <span className="font-medium">
                {selected.size} selected{selected.size > pageIds.filter((id) => isSelected(id)).length ? ' (incl. other pages/filters)' : ''}
              </span>
              <Button type="button" onClick={() => void bulkSetActive(true)} loading={bulkBusy} disabled={bulkBusy}>
                Activate
              </Button>
              <Button type="button" variant="ghost" onClick={() => void bulkSetActive(false)} disabled={bulkBusy}>
                Deactivate
              </Button>
              <button type="button" onClick={clear} className="text-xs underline text-[var(--color-muted)]">
                Clear
              </button>
            </div>
          ) : null}

          {filtered.length === 0 ? (
            <Card>
              <p className="text-sm text-[var(--color-muted)]">No services match your filters.</p>
            </Card>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] glass-flat">
                <table className="w-full text-sm">
                  <thead className="bg-[rgb(var(--edge-rgb)/0.05)] text-left">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={(e) => setMany(pageIds, e.target.checked)}
                          aria-label="Select all on this page"
                        />
                      </th>
                <th className="px-4 py-3 font-medium">Service</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Price / Cost</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Active</th>
                <th className="px-4 py-3 font-medium">Business</th>
                <th className="px-4 py-3 font-medium">Clients</th>
                {/*
                  TODO(Stage 4): the "Monthly revenue – profit" column slots in here once
                  recurringExpenses exist (joined by licenseSku) — see 04 "2026-06-09 backlog" #2.
                  Per-member SEAT counts fold into the "Clients" cell when seat tracking lands (08).
                */}
                <th className="px-4 py-3 font-medium text-right">Edit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--edge-rgb)/0.05)]">
              {pageItems.map((s) => (
                <tr key={s.id} className={isSelected(s.id) ? 'bg-[var(--color-accent)]/5' : undefined}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected(s.id)}
                      onChange={() => toggle(s.id)}
                      aria-label={`Select ${s.name}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <AvatarImage src={(s.logoBlobRef ? `/api/service-logo?serviceId=${encodeURIComponent(s.id)}` : s.logoUrl) || null} name={s.name} size={36} shape="square" alt={`${s.name} logo`} />
                      <div>
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-[var(--color-muted)]">
                          {s.shortName} · <span className="font-mono">{s.id}</span>
                        </div>
                        {s.clientFacingName ? (
                          <div className="text-xs text-[var(--color-muted)]">
                            Shown to clients: {s.clientFacingName}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {serviceTypeLabel(s.serviceType)}
                    {/* 04 #160: the tax category at a glance, so an unreviewed row is visible before the December review page exists. */}
                    {s.taxCategory ? (
                      <span className="block text-xs">{taxCategoryLabel(s.taxCategory)}</span>
                    ) : (
                      <span className="block text-xs text-[var(--tint-warn-text)]">Tax category not set</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    ${s.defaultClientPrice.toFixed(2)}
                    <span className="text-xs"> / ${s.defaultCostToYou.toFixed(2)}</span>
                    {s.pricingMode === 'margin' ? <span className="text-xs"> · margin</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    {s.active ? (
                      <span className="inline-flex items-center rounded-full bg-[var(--tint-green-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-green-text)]">
                        Active
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded-full bg-[rgb(var(--edge-rgb)/0.10)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted)]"
                        title={s.disabledAt ? `Disabled ${new Date(s.disabledAt).toLocaleString()}` : undefined}
                      >
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveToggle
                      active={s.active}
                      busy={busyId === s.id}
                      label={s.name}
                      onToggle={() => toggleActive(s)}
                    />
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    <div>{brandDisplayName(businesses.find((b) => b.id === s.defaultBillingEntity)?.name ?? s.defaultBillingEntity)}</div>
                    <div className="text-xs">
                      {s.availableToBusinessIds == null
                        ? 'All brands'
                        : s.availableToBusinessIds
                            .map((id) => brandDisplayName(businesses.find((b) => b.id === id)?.name ?? id))
                            .join(', ') + ' only'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {assignedCounts[s.id] ?? 0} client{(assignedCounts[s.id] ?? 0) === 1 ? '' : 's'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <PillButton size="lg" onClick={() => openEdit(s)} disabled={busyId === s.id}>
                        Edit
                      </PillButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
              </div>
              {totalPages > 1 ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[var(--color-muted)]">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="ghost" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
                      Prev
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmState?.kind === 'disable'}
        title={`Disable ${confirmLabelText} for ALL clients?`}
        confirmLabel="Disable"
        cancelLabel="Keep active"
        confirmVariant="danger"
        busy={confirmBusy}
        onConfirm={() => void runConfirmed()}
        onCancel={() => setConfirmState(null)}
      >
        <p>
          {confirmClientTotal === 0
            ? 'No clients currently hold an active assignment, so only the catalog entry is disabled.'
            : `${confirmClientTotal} client${confirmClientTotal === 1 ? '' : 's'} currently hold${confirmClientTotal === 1 ? 's' : ''} this and will be suspended: access is removed (delicensed), and each affected client is notified by email and an in-app notice.`}
        </p>
        {confirmAffectedPackages.length > 0 ? (
          <div className="space-y-1">
            <p className="font-medium">Also part of package{confirmAffectedPackages.length === 1 ? '' : 's'} with active holders:</p>
            <ul className="list-disc pl-5">
              {confirmAffectedPackages.map((p) => (
                <li key={p.packageId}>
                  {p.name} ({p.activeHolderCount} client{p.activeHolderCount === 1 ? '' : 's'})
                </li>
              ))}
            </ul>
            <p>
              A bundle and its price are NOT changed by disabling a member service. Your options: adjust each
              package's pricing on the Packages page, create a replacement package without this service and
              switch the holders to it (also on the Packages page), or proceed anyway. Those holders' member
              service is suspended either way, and they are told their bundle price is unchanged.
            </p>
          </div>
        ) : null}
        <p className="text-[var(--color-muted)]">
          Billing proration for the disabled period is applied later by the invoicing engine (Stage 4); nothing
          is billed or refunded right now. Re-enabling will offer to restore the suspended clients.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmState?.kind === 'enable'}
        title={`Re-enable ${confirmLabelText}?`}
        confirmLabel={cleanSlate ? 'Re-enable (clean slate)' : 'Re-enable and restore'}
        cancelLabel="Cancel"
        confirmVariant="primary"
        busy={confirmBusy}
        onConfirm={() => void runConfirmed()}
        onCancel={() => setConfirmState(null)}
      >
        <p>
          {confirmClientTotal} client{confirmClientTotal === 1 ? ' was' : 's were'} suspended when this was
          disabled{confirmDisabledDate ? ` on ${new Date(confirmDisabledDate).toLocaleDateString()}` : ''}.{' '}
          {confirmRestorableTotal > 0
            ? `Restoring re-adds access for ${confirmRestorableTotal} of them and notifies them.`
            : 'Restoring will not bring any of them back.'}
        </p>
        {confirmHeldTotal > 0 ? (
          <p>
            {confirmHeldTotal === 1
              ? '1 of those clients has a past due balance and stays suspended.'
              : `${confirmHeldTotal} of those clients have a past due balance and stay suspended.`}{' '}
            Re-enabling the service does not settle a debt, so they are not re-added and not notified.
          </p>
        ) : null}
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={cleanSlate}
            onChange={(e) => setCleanSlate(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Re-enable with a clean slate: leave those clients suspended (they are not notified; they can be
            reactivated individually later).
          </span>
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!promote}
        title={`Provision current holders of ${promote?.serviceName ?? ''}?`}
        confirmLabel="Provision now"
        cancelLabel="Not now"
        confirmVariant="primary"
        busy={promoteBusy}
        onConfirm={() => void runPromote()}
        onCancel={() => {
          setPromote(null);
          setInfo("Holders were not provisioned. You can run it any time from the service's Entra groups panel.");
        }}
      >
        <p>
          {promote
            ? `${promote.info.activeHolderCount} client${promote.info.activeHolderCount === 1 ? '' : 's'} currently hold${promote.info.activeHolderCount === 1 ? 's' : ''} this service (${promote.info.activeRowCount} assignment${promote.info.activeRowCount === 1 ? '' : 's'}, ${promote.info.unprovisionedActiveRowCount} not yet provisioned).`
            : null}
        </p>
        <p>
          Provisioning adds each client's primary contact to the per-brand Entra group (created automatically if
          needed) and marks their assignment provisioned. Same identities and rows; nobody is re-invited. Any
          failure is retried automatically and you are alerted.
        </p>
      </ConfirmDialog>
    </div>
  );
}

// ── s30 item 7 (08 Q31): the Services-editor Entra-groups panel. Read-only view of the REAL
//    per-(brand, service) bindings in `entraGroups` (the Q34 tombstone made provisioningGroupId
//    dead), a "link existing group" action, and the promotion follow-up button. Keys behavior on the
//    SAVED catalog model from the API, never the unsaved form select. ──
// ── License & seats (Stage-3 slice 2; 08 seat-drift resolution — the read-only cost-side panel) ──
// The Services editor is the client/revenue side; this panel shows the SAME derived seat picture
// the Expenses page owns for this service — never a second editable copy (single source of truth).
// It also surfaces service⇄expense COST DRIFT (catalog Cost to you vs the license's price per
// seat) with an explicit one-click "use the license's price in this form" — the value lands in the
// form field and rides the normal Save (configHistory), never a silent write.
function LicenseSeatsPanel({
  serviceId,
  serviceName,
  catalogCost,
  businessId,
  vendorName,
  businesses,
  onUseLicensePrice,
}: {
  serviceId: string;
  serviceName: string;
  catalogCost: string;
  businessId: string;
  vendorName: string;
  businesses: Business[];
  onUseLicensePrice: (price: string) => void;
}): JSX.Element {
  const [entries, setEntries] = useState<RecurringExpenseView[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<LicenseExpensePrompt | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.admin.listRecurringExpenses(serviceId);
        if (!cancelled) setEntries(res.entries);
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, reloadToken]);

  const licenses = (entries ?? []).filter((e) => e.expense.type === 'license');
  const subs = (entries ?? []).filter((e) => e.expense.type === 'subscription');
  const derived = licenses.find((e) => e.utilization?.basis === 'derived');
  const u = derived?.utilization;
  const cost = Number(catalogCost.trim());
  const drift =
    u?.pricePerSeat != null && Number.isFinite(cost) && Math.abs(cost - u.pricePerSeat) >= 0.005;

  return (
    <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-3">
      <div className="flex items-center gap-1">
        <p className="text-sm font-medium">License and seats</p>
        <InfoTip text="The cost side for this service: the linked license expense on the Expenses page. Purchased seats, the per-cycle amount, and availability all derive live from actual assignments (per-seat or block pricing set on the expense), so this can never disagree with reality. Read-only here; edit on the Expenses page." />
      </div>

      {err ? <p className="text-sm text-[var(--tint-red-text)]">{err}</p> : null}
      {entries == null && !err ? <p className="text-sm text-[var(--color-muted)]">Loading…</p> : null}

      {entries != null && derived && u ? (
        <div className="space-y-1 text-sm">
          <p>
            <strong>
              {u.inUse} of {u.purchased} in use
            </strong>
            {u.seatsAvailable > 0 ? ` (${u.seatsAvailable} available)` : ''}
            {u.suspendedSeats > 0 ? ` · ${u.suspendedSeats} suspended` : ''}
          </p>
          {(derived.expense.externalSeats ?? 0) > 0 ? (
            <p className="text-xs text-[var(--color-muted)]">
              {u.inUse - (derived.expense.externalSeats ?? 0)} assigned in the portal plus{' '}
              {derived.expense.externalSeats} extra (not in the portal).
            </p>
          ) : null}
          <p className="text-[var(--color-muted)]">
            {(u.seatsPerBlock ?? 1) > 1 ? `Blocks of ${u.seatsPerBlock}` : 'Per-seat'} at $
            {(u.pricePerSeat ?? 0).toFixed(2)}/seat
            {derived.expense.seatsFloor != null ? ` · floor ${derived.expense.seatsFloor} seats` : ''} →{' '}
            <strong>${(u.amountPerCycle ?? 0).toFixed(2)}</strong> per cycle (derived).
          </p>
          {drift ? (
            <div className="mt-1 rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-xs text-[var(--tint-warn-text)] space-y-1.5">
              <p>
                <strong>Cost drift:</strong> this form's Cost to you says ${Number.isFinite(cost) ? cost.toFixed(2) : catalogCost}
                /seat; the linked license says ${(u.pricePerSeat ?? 0).toFixed(2)}/seat. Nothing reconciles
                silently. Margin uses the catalog figure.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <PillButton tone="amber" onClick={() => onUseLicensePrice(String(u.pricePerSeat ?? 0))}>
                  Set Cost to you to ${(u.pricePerSeat ?? 0).toFixed(2)} (then Save)
                </PillButton>
                <Link to="/admin/expenses" className="underline">
                  or edit the license in Expenses
                </Link>
              </div>
            </div>
          ) : null}
          <p className="text-xs text-[var(--color-muted)]">
            Seat inputs (price, block, floor) are edited on the{' '}
            <Link to="/admin/expenses" className="underline">
              Expenses page
            </Link>
            .
          </p>
        </div>
      ) : null}

      {entries != null && !derived && licenses.length > 0 ? (
        <p className="text-sm text-[var(--color-muted)]">
          A legacy license links this service ({licenses[0].expense.description}) without per-seat
          tracking; edit it on the{' '}
          <Link to="/admin/expenses" className="underline">
            Expenses page
          </Link>{' '}
          to set a price per seat and move it onto the derived model.
        </p>
      ) : null}

      {entries != null && licenses.length === 0 ? (
        <div className="space-y-2 text-sm">
          <p className="text-[var(--color-muted)]">
            No license expense links this service; its seats and cost don't count in profitability
            yet{subs.length > 0 ? ' (a flat subscription links it instead)' : ''}.
          </p>
          {subs.length === 0 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setPrompt({
                  serviceId,
                  serviceName: serviceName || serviceId,
                  businessId,
                  suggestedPricePerSeat: Number.isFinite(cost) ? cost : null,
                  vendorName: vendorName.trim() === '' ? null : vendorName,
                  seatsInUse: 0,
                })
              }
            >
              Create linked license expense…
            </Button>
          ) : null}
        </div>
      ) : null}

      <CreateLicenseExpenseDialog
        prompt={prompt}
        businesses={businesses}
        onCreated={() => {
          setPrompt(null);
          setReloadToken((t) => t + 1);
        }}
        onClose={() => setPrompt(null)}
      />
    </div>
  );
}

function EntraGroupsPanel({
  serviceId,
  formModel,
  reloadToken,
  onRequestProvision,
}: {
  serviceId: string;
  formModel: ProvisioningModel;
  reloadToken: number;
  onRequestProvision: (info: ServiceGroupsResponse) => void;
}): JSX.Element {
  const [info, setInfo] = useState<ServiceGroupsResponse | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [linkBusinessId, setLinkBusinessId] = useState('');
  const [linkGroupId, setLinkGroupId] = useState('');
  const [linking, setLinking] = useState(false);

  const loadGroups = async (): Promise<void> => {
    try {
      const gi = await api.admin.getServiceGroups(serviceId);
      setInfo(gi);
      setLinkBusinessId((prev) =>
        prev && gi.unboundBusinesses.some((b) => b.businessId === prev)
          ? prev
          : (gi.unboundBusinesses[0]?.businessId ?? ''),
      );
    } catch (e) {
      setPanelError(apiErrorMessage(e));
    }
  };

  useEffect(() => {
    void loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, reloadToken]);

  const link = async (): Promise<void> => {
    if (!linkBusinessId || !linkGroupId.trim()) return;
    setLinking(true);
    setPanelError(null);
    try {
      await api.admin.linkServiceGroup(serviceId, linkBusinessId, linkGroupId.trim());
      setLinkGroupId('');
      await loadGroups();
    } catch (e) {
      setPanelError(apiErrorMessage(e));
    } finally {
      setLinking(false);
    }
  };

  const grandfatherNote =
    formModel === 'manual' && info && (info.bindings.length > 0 || info.activeRowCount > 0);

  return (
    <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-4 space-y-4">
      <p className="text-sm font-medium">
        Entra groups
        <InfoTip text="Each brand gets its own Entra security group for this service; membership is what licenses and grants access. Groups are created automatically when the first holder is provisioned under a brand. Link an existing Entra group instead if this service should reuse one you already manage. Manual services have no group and are set up by hand." />
      </p>

      {info === null && !panelError ? (
        <p className="text-xs text-[var(--color-muted)]">Loading group bindings...</p>
      ) : null}

      {info ? (
        <div className="space-y-2">
          {info.bindings.map((b) => (
            <div key={b.businessId} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
              <span className="font-medium">{brandDisplayName(b.businessName)}</span>
              <span>{b.displayName}</span>
              <span className="font-mono text-xs text-[var(--color-muted)]">{b.entraGroupId}</span>
              <span className="text-xs text-[var(--color-muted)]">
                linked {new Date(b.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
          {info.unboundBusinesses.map((b) => (
            <div key={b.businessId} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="font-medium">{brandDisplayName(b.businessName)}</span>
              <span className="text-xs text-[var(--color-muted)]">
                No group yet. Created automatically on the first provisioned assignment.
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {info && info.unboundBusinesses.length > 0 ? (
        <div className="space-y-2 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-3">
          <p className="text-xs font-medium">
            Link an existing Entra group
            <InfoTip text="Paste the group's object ID from the Entra portal. The group is verified against Entra before anything is written, and its real name is captured. Each group can serve exactly one binding." />
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {info.unboundBusinesses.length > 1 ? (
              <Select
                value={linkBusinessId}
                onChange={setLinkBusinessId}
                options={info.unboundBusinesses.map((b) => [b.businessId, brandDisplayName(b.businessName)] as const)}
              />
            ) : (
              <p className="text-sm md:self-center">{brandDisplayName(info.unboundBusinesses[0].businessName)}</p>
            )}
            <Input value={linkGroupId} onChange={setLinkGroupId} placeholder="Entra group object ID (GUID)" mono />
            <Button type="button" variant="ghost" onClick={() => void link()} loading={linking} disabled={linking || !linkGroupId.trim()}>
              Link group
            </Button>
          </div>
        </div>
      ) : null}

      {info && info.provisioningModel !== 'manual' && info.unprovisionedActiveRowCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-3">
          <p className="text-xs text-[var(--color-muted)]">
            {info.unprovisionedActiveRowCount} active assignment{info.unprovisionedActiveRowCount === 1 ? ' is' : 's are'} not
            yet provisioned.
          </p>
          <Button type="button" variant="ghost" onClick={() => onRequestProvision(info)}>
            Provision current holders
          </Button>
        </div>
      ) : null}

      {grandfatherNote ? (
        <p className="text-xs text-[var(--color-muted)]">
          Manual model with existing holders: holders provisioned earlier are grandfathered. They keep their
          access and group memberships, and suspending or cancelling one still revokes it through its stored
          group. Only future assignments behave manually.
        </p>
      ) : null}

      {panelError ? (
        <div className="rounded-md bg-[var(--tint-red-bg-soft)] border border-[var(--tint-red-border)] px-3 py-2 text-xs text-[var(--tint-red-text)]">{panelError}</div>
      ) : null}
    </div>
  );
}


function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  mono,
  list,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  mono?: boolean;
  list?: string;
}): JSX.Element {
  return (
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      list={list}
      step={type === 'number' ? 'any' : undefined}
      className={[
        'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]',
        mono ? 'font-mono text-xs' : '',
      ].join(' ')}
    />
  );
}

function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
  disabled?: boolean;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)] disabled:opacity-60"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
